import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Paper, Typography, Avatar, IconButton, TextField,
  Stack, Badge, Divider, CircularProgress, Tooltip, Button,
  Dialog, DialogContent
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ForumIcon from '@mui/icons-material/Forum';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { sendMessage, toggleChat, setCurrentChat, getChats, deleteChat, markAsRead } from '../../actions/chat';
import { urlImg } from '../common/helpers';

// Nombre legible del tema de un chat (servicio/página) y su categoría de respaldo
const temaTitulo = (chat) => chat?.tema?.nombre || chat?.tema?.categoria || '';
// Subtítulo con la categoría solo cuando aporta información nueva respecto al título
const temaCategoria = (chat) => (chat?.tema?.nombre && chat?.tema?.categoria) ? chat.tema.categoria : '';

const ChatFloating = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { isOpen, currentChat, messages, chats, loading, onlineUsers } = useSelector(state => state.chat);
  const authUser = useSelector(state => state.user.user);
  
  const getStrId = (id) => {
    if (!id) return '';
    if (typeof id === 'string') return id;
    if (typeof id === 'object' && id._id) return id._id.toString();
    return String(id);
  };

  const otherParticipant = currentChat?.participants.find(p => getStrId(p._id) !== getStrId(authUser?._id));
  const isOnline = otherParticipant && onlineUsers.some(id => getStrId(id) === getStrId(otherParticipant._id));
  // El proveedor es quien puede cotizar; la cotización formal solo existe en el chat completo
  const soyProveedor = currentChat && getStrId(currentChat.provider) === getStrId(authUser?._id);
  const soyCliente = currentChat && !soyProveedor;
  // El cliente solo puede abrir/ver la cotización desde el chat completo
  const hayCotizacion = messages.some(m => m.type === 'quote');

  const irChatCompleto = () => {
    navigate('/mensajes');
    dispatch(toggleChat(false));
  };
  
  const [text, setText] = useState('');
  const [deletingChat, setDeletingChat] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 100);
  };

  const totalUnread = chats.reduce((acc, chat) => {
    const myId = getStrId(authUser?._id);
    const count = chat.unreadCount?.[myId] || 0;
    return acc + count;
  }, 0);

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      dispatch(getChats());
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (isOpen && messages.length > 0) {
      scrollToBottom();
    }
    if (isOpen && currentChat) {
      dispatch(markAsRead(currentChat._id));
    }
  }, [messages, isOpen, currentChat, dispatch]);

  const handleSend = (e) => {
    e.preventDefault();
    if (text.trim() && currentChat) {
      dispatch(sendMessage(currentChat._id, text));
      setText('');
    }
  };

  // LA CONDICIÓN DEBE IR AQUÍ, DESPUÉS DE TODOS LOS HOOKS (useState, useEffect, etc.)
  if (location.pathname === '/mensajes') return null;
  // No mostrar el chat flotante en el panel de administración.
  if (location.pathname.startsWith('/admin')) return null;

  if (!authUser) return null;

  const deletingOther = deletingChat?.participants?.find(p => String(p._id) !== String(authUser?._id));

  return (
    <>
      {/* Modal confirmar eliminar conversación */}
      <Dialog open={!!deletingChat} onClose={() => setDeletingChat(null)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
          <Box sx={{
            width: 60, height: 60, borderRadius: '50%', bgcolor: '#FEE2E2',
            display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2
          }}>
            <DeleteOutlineIcon sx={{ fontSize: 30, color: '#EF4444' }} />
          </Box>
          <Typography variant="h6" fontWeight={700} color="#0E2465" mb={1}>
            ¿Eliminar conversación?
          </Typography>
          <Typography variant="body2" color="#64748B" mb={3}>
            Se eliminará la conversación con <strong>{deletingOther?.name} {deletingOther?.apellido}</strong>. Esta acción no se puede deshacer.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="outlined"
              onClick={() => setDeletingChat(null)}
              sx={{ textTransform: 'none', borderRadius: '8px', px: 3, borderColor: '#E2E8F0', color: '#64748B' }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                dispatch(deleteChat(deletingChat._id));
                setDeletingChat(null);
              }}
              sx={{ textTransform: 'none', borderRadius: '8px', px: 3, bgcolor: '#EF4444', '&:hover': { bgcolor: '#DC2626' } }}
            >
              Eliminar
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Botón Flotante Principal */}
      {!isOpen && (
        <IconButton
          onClick={() => dispatch(toggleChat(true))}
          sx={{
            position: 'fixed', bottom: 30, right: 30,
            bgcolor: '#0E2465', color: 'white',
            width: 60, height: 60,
            boxShadow: '0 8px 32px rgba(14, 36, 101, 0.4)',
            '&:hover': { bgcolor: '#163185', transform: 'scale(1.05)' },
            transition: 'all 0.3s ease',
            zIndex: 9999
          }}
        >
          <Badge badgeContent={totalUnread} color="error">
            <ForumIcon sx={{ fontSize: 30 }} />
          </Badge>
        </IconButton>
      )}

      {/* Ventana de Chat */}
      {isOpen && (
        <Paper
          elevation={24}
          sx={{
            position: 'fixed', bottom: 100, right: 30,
            width: { xs: '90vw', sm: 400 }, height: 600,
            borderRadius: '24px', overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
            zIndex: 9999, border: '1px solid #E2E8F0'
          }}
        >
          {/* Header */}
          <Box sx={{ p: 2, bgcolor: '#0E2465', color: 'white' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                {currentChat && (
                  <IconButton size="small" onClick={() => dispatch(setCurrentChat(null))} sx={{ color: 'white' }}>
                    <ArrowBackIcon />
                  </IconButton>
                )}
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" noWrap sx={{ fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.2, maxWidth: 210 }}>
                    {currentChat ? (temaTitulo(currentChat) || 'Conversación') : 'Mensajes'}
                  </Typography>
                  {currentChat && temaCategoria(currentChat) && (
                    <Typography variant="caption" noWrap sx={{ display: 'block', opacity: 0.75, maxWidth: 210 }}>
                      {temaCategoria(currentChat)}
                    </Typography>
                  )}
                </Box>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Ver en pantalla completa">
                  <IconButton size="small" onClick={irChatCompleto} sx={{ color: 'white' }}>
                    <OpenInFullIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={() => dispatch(toggleChat(false))} sx={{ color: 'white' }}>
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Stack>
          </Box>

          {!currentChat ? (
            /* Lista de Chats */
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: '#F8FAFC' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress size={24} /></Box>
              ) : chats.length > 0 ? (
                chats.map(chat => {
                  const other = chat.participants.find(p => String(p._id) !== String(authUser?._id));
                  const userIsOnline = other && onlineUsers.some(id => String(id) === String(other?._id));
                  const avatar = other?.avatar ? (other.avatar.startsWith('http') ? other.avatar : `${urlImg}/users/${other._id}/${other.avatar}`) : '';
                  
                  return (
                    <Paper 
                      key={chat._id}
                      onClick={() => dispatch(setCurrentChat(chat))}
                      elevation={0}
                      sx={{ 
                        p: 2, mb: 1, borderRadius: '16px', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 2,
                        bgcolor: 'white', border: '1px solid #F1F5F9',
                        '&:hover': { bgcolor: '#F1F5F9', transform: 'translateY(-2px)' },
                        transition: 'all 0.2s'
                      }}
                    >
                      <Avatar src={avatar} sx={{ width: 48, height: 48 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B' }} noWrap>
                          {other?.name} {other?.apellido}
                        </Typography>
                        {temaTitulo(chat) && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, my: 0.25 }}>
                            <LocalOfferOutlinedIcon sx={{ fontSize: 12, color: '#7495F8', flexShrink: 0 }} />
                            <Typography variant="caption" noWrap sx={{ color: '#7495F8', fontWeight: 600 }}>
                              {temaTitulo(chat)}
                            </Typography>
                          </Box>
                        )}
                        <Typography variant="caption" noWrap sx={{ color: '#64748B', display: 'block' }}>
                          {chat.lastMessage?.content || 'Sin mensajes'}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {chat.unreadCount?.[authUser?._id] > 0 && (
                          <Badge badgeContent={chat.unreadCount[authUser._id]} color="primary" sx={{ mr: 1 }} />
                        )}
                        <Tooltip title="Eliminar conversación">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeletingChat(chat);
                            }}
                            sx={{
                              color: '#CBD5E1',
                              '&:hover': { color: '#EF4444', bgcolor: '#FEE2E2' }
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Paper>
                  );
                })
              ) : (
                <Box sx={{ textAlign: 'center', mt: 4, opacity: 0.5 }}>
                  <ForumIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="body2">No tienes conversaciones aún</Typography>
                </Box>
              )}
            </Box>
          ) : (
            /* Detalle del Chat */
            <>
              <Box sx={{ px: 2, py: 1, bgcolor: 'white', borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar 
                  src={currentChat.participants.find(p => p._id !== authUser?._id)?.avatar ? (currentChat.participants.find(p => p._id !== authUser?._id).avatar.startsWith('http') ? currentChat.participants.find(p => p._id !== authUser?._id).avatar : `${urlImg}/users/${currentChat.participants.find(p => p._id !== authUser?._id)._id}/${currentChat.participants.find(p => p._id !== authUser?._id).avatar}`) : ''} 
                  sx={{ width: 36, height: 36 }} 
                />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B', fontSize: '0.9rem' }}>
                    {otherParticipant?.name} {otherParticipant?.apellido}
                  </Typography>
                  <Typography variant="caption" sx={{ color: isOnline ? '#22C55E' : '#64748B', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 6, height: 6, bgcolor: isOnline ? '#22C55E' : '#94A3B8', borderRadius: '50%' }} />
                    {isOnline ? 'Online' : 'Activo'}
                  </Typography>
                </Box>
              </Box>

              {soyProveedor && (
                <Box
                  onClick={irChatCompleto}
                  sx={{
                    px: 2, py: 1, bgcolor: '#EEF2FF', borderBottom: '1px solid #E2E8F0',
                    display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                    '&:hover': { bgcolor: '#E0E7FF' }
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 18, color: '#0E2465', flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ color: '#0E2465', lineHeight: 1.3 }}>
                    Para enviar una <strong>cotización formal</strong> abre el chat completo.
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#7495F8', fontWeight: 700, ml: 'auto', whiteSpace: 'nowrap' }}>
                    Abrir
                  </Typography>
                </Box>
              )}

              {soyCliente && hayCotizacion && (
                <Box
                  onClick={irChatCompleto}
                  sx={{
                    px: 2, py: 1, bgcolor: '#EEF2FF', borderBottom: '1px solid #E2E8F0',
                    display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                    '&:hover': { bgcolor: '#E0E7FF' }
                  }}
                >
                  <DescriptionOutlinedIcon sx={{ fontSize: 18, color: '#0E2465', flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ color: '#0E2465', lineHeight: 1.3 }}>
                    Recibiste una <strong>cotización</strong>. Ábrela en el chat completo para verla.
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#7495F8', fontWeight: 700, ml: 'auto', whiteSpace: 'nowrap' }}>
                    Abrir
                  </Typography>
                </Box>
              )}

              <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: '#F8FAFC', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Divider sx={{ flex: 1 }} />
                  <Typography variant="caption" sx={{ px: 1, color: '#94A3B8', fontWeight: 600 }}>Hoy</Typography>
                  <Divider sx={{ flex: 1 }} />
                </Box>

                {messages.map((msg, index) => {
                  const getStrId = (id) => (typeof id === 'object' && id?._id ? id._id.toString() : String(id || ''));
                  const msgSenderId = getStrId(msg.sender);
                  const myId = getStrId(authUser?._id);
                  const isMe = msgSenderId === myId;
                  
                  return (
                    <Box key={msg._id || index} sx={{ alignSelf: isMe ? 'flex-end' : 'flex-start', mb: 2, maxWidth: '80%' }}>
                      <Box sx={{ 
                        p: 1.5, borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                        bgcolor: isMe ? '#0E2465' : 'white',
                        color: isMe ? 'white' : '#1E293B',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        border: isMe ? 'none' : '1px solid #F1F5F9'
                      }}>
                        <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>{msg.content}</Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7, textAlign: 'right', fontSize: '0.65rem' }}>
                          {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
                <div ref={messagesEndRef} />
              </Box>

              <Box component="form" onSubmit={handleSend} sx={{ p: 2, bgcolor: 'white', borderTop: '1px solid #F1F5F9' }}>
                <Stack direction="row" spacing={1}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Escribe un mensaje..."
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    autoComplete="off"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#F8FAFC' } }}
                  />
                  <IconButton type="submit" disabled={!text.trim()} sx={{ bgcolor: '#0E2465', color: 'white', '&:hover': { bgcolor: '#163185' } }}>
                    <SendIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
            </>
          )}
        </Paper>
      )}
    </>
  );
};

export default ChatFloating;
